<template>
  <!--
    Member "Activity sky" section (M4C-34) — shown on another member's profile.
    A compact heading + "How it works" link, then one horizontal row per
    constellation the member has started: shared renderer preview (M4C-26, `row`
    variant — no hardcoded geometry) + name + category + earned-star tally.

    The page only mounts this when the member has progress and isn't anonymous,
    so this component assumes a non-empty `rows` and renders no empty state.
  -->
  <div class="pb-[8px]">
    <!-- Section header -->
    <div class="mb-[14px] flex items-center justify-between gap-[12px]">
      <h2 class="m-0 font-app text-card-title text-fontColor">
        {{ t('profile.activity_sky') }}
      </h2>
      <NuxtLink
        :to="howItWorksTo"
        class="shrink-0 font-app text-microcopy text-link no-underline hover:underline"
      >
        {{ t('account.how_it_works') }}
      </NuxtLink>
    </div>

    <!-- Constellation rows -->
    <div class="divide-y divide-fontColor-w90 rounded-lg border border-fontColor-w80">
      <div
        v-for="row in rows"
        :key="row.constellation_id"
        class="flex items-center gap-[14px] px-[16px] py-[14px] md:gap-[20px] md:px-[20px]"
      >
        <!-- Renderer preview -->
        <div class="w-[64px] shrink-0 md:w-[84px]">
          <GamiConstellation
            :stars="row.constellation.stars_data"
            :segments="row.constellation.segments_data"
            :progress="row.stars"
            :name="row.name"
            variant="row"
            :animated="false"
          />
        </div>

        <!-- Name + category + star tally. Stacks on mobile (name keeps full
             width, tally drops below); name left / tally right on desktop. -->
        <div
          class="flex min-w-0 flex-1 flex-col gap-[8px] md:flex-row md:items-center md:justify-between md:gap-[14px]"
        >
          <div class="min-w-0">
            <h3 class="m-0 truncate font-app text-body-strong text-fontColor">
              {{ row.name }}
            </h3>
            <p class="m-0 mt-[2px] truncate font-inter text-microcopy text-fontColor-w60">
              {{ categoryLabel(row.category) }}
            </p>
          </div>

          <!-- Earned-star tally -->
          <div
            class="flex shrink-0 items-center gap-[3px]"
            :aria-label="`${row.stars_earned} / ${row.stars_total} ${t('profile.activity_sky_stars')}`"
          >
            <svg
              v-for="i in row.stars_total"
              :key="i"
              viewBox="0 0 24 24"
              class="h-[15px] w-[15px] md:h-[16px] md:w-[16px]"
              :class="i <= row.stars_earned ? 'fill-star' : 'fill-fontColor-w80'"
              aria-hidden="true"
            >
              <path :d="STAR_PATH" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { MemberConstellationRow } from '@/types/gamification';

defineProps<{ rows: MemberConstellationRow[] }>();

const { t, te } = useI18n();
const localePath = useLocalePath();

const howItWorksTo = computed(() => localePath('/account/star-glazing'));

/** Generic upward 5-point star glyph (UI icon — not constellation geometry). */
const STAR_PATH =
  'M12 2.5l2.94 5.96 6.58.96-4.76 4.64 1.12 6.55L12 18.56l-5.88 3.09 1.12-6.55-4.76-4.64 6.58-.96z';

/**
 * Human category label, backed by i18n; falls back to a title-cased key so an
 * unknown category from the live API (M4C-30) still renders. Mirrors the
 * Activity sky overview card (M4C-20).
 */
function categoryLabel(category: string): string {
  const key = `gamification.category.${category}`;
  if (te(key)) {
    return t(key);
  }
  return category ? category.charAt(0).toUpperCase() + category.slice(1) : '';
}
</script>
